import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { apiError, assetUrl } from "@/lib/api";
import { toast } from "sonner";
import { hasManualLang } from "@/i18n";
import { setLang } from "@/i18n";
import { translateDeep, trStr, activeDict } from "@/lib/translate";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Diamond, CircleNotch, Envelope, WhatsappLogo, ArrowRight, Storefront as StoreIcon, ShoppingCart, Package } from "@phosphor-icons/react";
import { StoreBlock, StoreHeader, StoreFooter, textOn, fontStack, ensureFontsLoaded, collectFonts, catSlug } from "@/lib/storeBlocks";
import { motion } from "framer-motion";

const ANIM = {
  "fade-up": { initial: { opacity: 0, y: 36 }, whileInView: { opacity: 1, y: 0 } },
  "fade": { initial: { opacity: 0 }, whileInView: { opacity: 1 } },
  "zoom": { initial: { opacity: 0, scale: 0.92 }, whileInView: { opacity: 1, scale: 1 } },
};
function AnimatedBlock({ anim, children }) {
  const a = ANIM[anim];
  if (!a) return children;
  return <motion.div initial={a.initial} whileInView={a.whileInView} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6, ease: "easeOut" }}>{children}</motion.div>;
}

export default function Storefront({ slugOverride, preview }) {
  const params = useParams();
  const [resolvedSlug, setResolvedSlug] = useState(slugOverride || params.slug);
  const slug = slugOverride || params.slug || resolvedSlug;
  const pageSlug = params.pageSlug;
  const collectionCat = params.cat;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("q") || "";
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    if (preview) {
      api.get(`/store/preview`)
        .then((r) => { setData(r.data); setResolvedSlug(r.data?.store?.slug); })
        .catch((e) => setErr(apiError(e)));
      return;
    }
    // Capture rep + promo attribution from a shared link (?rep=Nombre&rep_tel=..&promo=CODE)
    const q = new URLSearchParams(window.location.search);
    const rep = q.get("rep"); const repTel = q.get("rep_tel") || q.get("rep_phone"); const promo = q.get("promo");
    try {
      if (rep || repTel) localStorage.setItem(`hephy_rep_${slug}`, JSON.stringify({ name: rep || "", phone: repTel || "" }));
      if (promo) localStorage.setItem(`hephy_promo_${slug}`, promo.toUpperCase());
    } catch { /* sandboxed */ }
    api.get(`/public/store/${slug}`, { withCredentials: false })
      .then((r) => {
        setData(r.data);
        // Owner's default store language applies unless the buyer already chose one.
        if (r.data?.store?.language && !hasManualLang()) i18n.changeLanguage(r.data.store.language);
      })
      .catch((e) => setErr(apiError(e)));
    const token = localStorage.getItem(`hephy_cart_${slug}`);
    if (token) api.get(`/public/cart/${token}`, { withCredentials: false }).then((r) => setCartCount(r.data.count || 0)).catch(() => {});
  }, [slug, preview]);

  // Keep the whole page (incl. any horizontal scroll area) painted with the store theme color.
  useEffect(() => {
    const col = data?.store?.theme_color || "#0B0B0F";
    const prevHtml = document.documentElement.style.background;
    const prevBody = document.body.style.background;
    document.documentElement.style.background = col;
    document.body.style.background = col;
    return () => { document.documentElement.style.background = prevHtml; document.body.style.background = prevBody; };
  }, [data]);

  // Per-page SEO (title + meta description).
  useEffect(() => {
    if (!data?.store) return;
    const st = data.store;
    const dl = (i18n.language || st.language || "en").slice(0, 2).toLowerCase();
    const d = activeDict(st, dl);
    const s = d ? translateDeep(st.site || {}, d) : (st.site || {});
    ensureFontsLoaded(collectFonts(s));
    const pgs = Array.isArray(s.pages) ? s.pages : [];
    const cur = pgs.length ? (pageSlug ? pgs.find((p) => p.slug === pageSlug) : (pgs.find((p) => !p.slug) || pgs[0])) : null;
    const seo = (cur && cur.seo) || {};
    const nm = trStr(st.name, d);
    document.title = seo.title || (cur && cur.title ? `${cur.title} — ${nm}` : nm || "Tienda");
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.content = seo.description || trStr(st.tagline, d) || trStr(st.bio, d) || "";
    // Open Graph / Twitter tags (for JS-capable scrapers; non-JS scrapers use /api/public/share/:slug)
    const ogImgId = st.share_image_asset_id || st.banner_asset_id || st.logo_asset_id;
    const ogImg = ogImgId ? `${window.location.origin}/api/assets/${ogImgId}` : "";
    const setMeta = (attr, key, val) => {
      if (!val) return;
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", val);
    };
    setMeta("property", "og:title", document.title);
    setMeta("property", "og:description", m.content);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:image", ogImg);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:image", ogImg);
  }, [data, pageSlug, i18n.language]);

  if (err) return (
    <div className="min-h-screen bg-[#0B0B0F] flex flex-col items-center justify-center text-neutral-400 gap-3 px-6 text-center" data-testid="store-error">
      <StoreIcon size={40} weight="duotone" className="text-neutral-600" />
      <p>{err}</p>
      <Link to="/" className="text-gold underline text-sm">{t("common.goToHephy")}</Link>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-[#0B0B0F] flex items-center justify-center"><CircleNotch size={30} className="animate-spin text-gold" /></div>;

  const { store, products: rawProducts } = data;
  const accent = store.accent || "#C6A15B";
  const bg = store.theme_color || "#0B0B0F";
  const primaryLang = (store.language || "en").toLowerCase();
  const displayLang = (i18n.language || primaryLang).slice(0, 2).toLowerCase();
  const dict = activeDict(store, displayLang);
  const site = dict ? translateDeep(store.site || {}, dict) : (store.site || {});
  const products = dict ? rawProducts.map((p) => translateDeep(p, dict)) : rawProducts;
  const tmpl = site.template || "classic";
  const headingFont = fontStack(site.font);
  const defaultSections = [
    { key: "hero", enabled: true }, { key: "products", enabled: true },
    { key: "about", enabled: !!(site.about || store.bio) }, { key: "testimonials", enabled: false },
    { key: "gallery", enabled: false }, { key: "contact", enabled: !!(store.contact_email || store.whatsapp) },
  ];
  const sections = (Array.isArray(site.sections) && site.sections.length) ? site.sections : defaultSections;
  const heroTitle = site.hero_title || trStr(store.name, dict);
  const heroSub = site.hero_subtitle || trStr(store.tagline, dict);
  const aboutText = site.about || trStr(store.bio, dict);
  const testimonials = Array.isArray(site.testimonials) ? site.testimonials : [];
  const gallery = products.filter((p) => p.base_url).slice(0, 6);

  const blocks = Array.isArray(site.blocks) ? site.blocks : [];
  const pages = Array.isArray(site.pages) ? site.pages : [];
  const palette = site.palette || {};
  const titleColorG = palette.text || undefined;
  const rootColor = palette.text || textOn(bg);
  const bodyFontG = site.body_font ? fontStack(site.body_font) : undefined;
  const btnFontG = site.btn_font ? fontStack(site.btn_font) : undefined;
  const linkFontG = site.link_font ? fontStack(site.link_font) : undefined;
  const currentPage = pages.length
    ? (pageSlug ? pages.find((p) => p.slug === pageSlug) : (pages.find((p) => !p.slug) || pages[0]))
    : null;
  const pageBlocks = currentPage ? (currentPage.blocks || []) : blocks;
  const header = (currentPage && currentPage.header) ? currentPage.header : site.header;
  const footer = site.footer;
  const hasHeader = header && header.enabled !== false;
  const blockCtx = { store, products, slug, accent, bg, headingFont, titleColor: titleColorG, bodyFont: bodyFontG, btnBg: palette.btnBg || undefined, btnText: palette.btnText || undefined, btnFont: btnFontG, linkColor: palette.link || undefined, linkFont: linkFontG, t, interactive: true, onAdded: setCartCount, searchQuery, lang: displayLang, primaryLang, onLang: (l) => setLang(l), translate: (o) => (dict ? translateDeep(o, dict) : o), onSearch: (term) => navigate(`/tienda/${slug}${term ? `?q=${encodeURIComponent(term)}` : ""}`) };


  const renderSection = (key) => {
    switch (key) {
      case "hero":
        return (
          <div key="hero" data-testid="sf-hero">
            <div className="relative">
              {store.banner_asset_id ? (
                <div className={`${tmpl === "bold" ? "h-64 sm:h-80" : "h-52 sm:h-64"} w-full overflow-hidden`}>
                  <img src={assetUrl(`/api/assets/${store.banner_asset_id}`)} alt="" className={`w-full h-full object-cover ${tmpl === "minimal" ? "opacity-90" : "opacity-70"}`} />
                </div>
              ) : (
                <div className="h-40 sm:h-52 w-full" style={{ background: `linear-gradient(135deg, ${bg} 0%, ${accent}22 100%)` }} />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            </div>
            <div className="max-w-6xl mx-auto px-6 -mt-16 relative">
              <div className={`flex items-end gap-5 ${tmpl === "minimal" ? "flex-col items-center text-center" : ""}`}>
                <div className="w-28 h-28 rounded-2xl border-2 shrink-0 overflow-hidden bg-neutral-900 flex items-center justify-center" style={{ borderColor: accent }}>
                  {store.logo_asset_id
                    ? <img src={assetUrl(`/api/assets/${store.logo_asset_id}`)} alt={store.name} className="w-full h-full object-cover" />
                    : <Diamond size={40} weight="fill" style={{ color: accent }} />}
                </div>
                <div className="pb-2 flex-1 min-w-0">
                  <h1 className={`${tmpl === "bold" ? "text-4xl sm:text-5xl uppercase" : "text-3xl sm:text-4xl"} font-bold tracking-tight truncate`} style={{ fontFamily: headingFont }} data-testid="store-name">{heroTitle}</h1>
                  {heroSub && <p className="text-neutral-300 mt-1 text-sm sm:text-base">{heroSub}</p>}
                  {site.hero_cta && (
                    <a href="#productos" className="inline-flex items-center gap-2 rounded-full px-5 py-2 mt-4 text-sm font-semibold text-black" style={{ backgroundColor: accent }} data-testid="sf-hero-cta">{site.hero_cta} <ArrowRight size={14} /></a>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      case "about":
        return aboutText ? (
          <div key="about" className="max-w-6xl mx-auto px-6 mt-12" data-testid="sf-about">
            <h2 className="text-lg font-semibold mb-3" style={{ color: accent, fontFamily: headingFont }}>{site.about_title || "Sobre nosotros"}</h2>
            <p className="text-neutral-400 max-w-2xl leading-relaxed text-sm whitespace-pre-line">{aboutText}</p>
          </div>
        ) : null;
      case "testimonials":
        return testimonials.length ? (
          <div key="testimonials" className="max-w-6xl mx-auto px-6 mt-12" data-testid="sf-testimonials">
            <h2 className="text-lg font-semibold mb-5" style={{ color: accent, fontFamily: headingFont }}>{site.testimonials_title || "Lo que dicen"}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {testimonials.map((tm, i) => (
                <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                  <p className="text-sm text-neutral-300 italic leading-relaxed">“{tm.text}”</p>
                  <p className="text-xs mt-3 font-semibold" style={{ color: accent }}>{tm.name}</p>
                </div>
              ))}
            </div>
          </div>
        ) : null;
      case "gallery":
        return gallery.length ? (
          <div key="gallery" className="max-w-6xl mx-auto px-6 mt-12" data-testid="sf-gallery">
            <h2 className="text-lg font-semibold mb-5" style={{ color: accent, fontFamily: headingFont }}>{site.gallery_title || "Galería"}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {gallery.map((p) => (
                <div key={p.id} className="aspect-square rounded-xl overflow-hidden bg-neutral-900">
                  <img src={assetUrl(p.base_url)} alt={p.name} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        ) : null;
      case "contact":
        return (store.contact_email || store.whatsapp) ? (
          <div key="contact" className="max-w-6xl mx-auto px-6 mt-12" data-testid="sf-contact">
            <h2 className="text-lg font-semibold mb-4" style={{ color: accent, fontFamily: headingFont }}>{site.contact_title || "Contáctanos"}</h2>
            <div className="flex flex-wrap gap-3">
              {store.contact_email && (
                <a href={`mailto:${store.contact_email}`} data-testid="store-email-btn" className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-black" style={{ backgroundColor: accent }}><Envelope size={16} /> {t("common.contact")}</a>
              )}
              {store.whatsapp && (
                <a href={`https://wa.me/${store.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" data-testid="store-wa-btn" className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium border border-white/20 hover:bg-white/5"><WhatsappLogo size={16} /> {t("common.whatsapp")}</a>
              )}
            </div>
          </div>
        ) : null;
      case "products":
      default:
        return (
          <div key="products" id="productos" className="max-w-6xl mx-auto px-6 mt-12 pb-20" data-testid="sf-products">
            <h2 className="text-lg font-semibold mb-5" style={{ color: accent, fontFamily: headingFont }}>{t("store.products")}</h2>
            {products.length === 0 ? (
              <div className="border border-dashed border-white/15 rounded-2xl p-16 text-center text-neutral-500" data-testid="store-empty">{t("store.empty")}</div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="store-products-grid">
                {products.map((p) => (
                  (p.catalog_type && p.catalog_type !== "custom3d")
                    ? <SimpleProductCard key={p.id} p={p} slug={slug} accent={accent} tmpl={tmpl} t={t} onAdded={setCartCount} />
                    : (
                  <Link key={p.id} to={`/tienda/${slug}/producto/${p.id}`} data-testid={`store-product-${p.id}`}
                    className={`group overflow-hidden border border-white/10 bg-white/[0.03] hover:border-white/25 transition-all hover:-translate-y-1 ${tmpl === "minimal" ? "rounded-none" : "rounded-2xl"}`}>
                    <div className="aspect-square bg-neutral-900 relative overflow-hidden">
                      {p.base_url
                        ? <img src={assetUrl(p.base_url)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        : <div className="w-full h-full flex items-center justify-center"><Diamond size={36} weight="duotone" className="text-neutral-700" /></div>}
                      {p.has_3d && <span className="absolute top-3 left-3 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold text-black" style={{ backgroundColor: accent }}>3D</span>}
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold truncate">{p.name}</h3>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-sm text-neutral-400">{p.price > 0 ? `${t("store.from")} $${Number(p.price).toLocaleString()}` : t("store.customizable")}</span>
                        <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: accent }}>{t("store.personalize")} <ArrowRight size={13} /></span>
                      </div>
                    </div>
                  </Link>
                    )
                ))}
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden storefront-brand" style={{ backgroundColor: bg, color: rootColor, fontFamily: headingFont }} data-testid="storefront-page">
      {preview && <div className="sticky top-0 z-50 bg-gold text-obsidian text-center text-xs font-semibold py-2" data-testid="preview-banner">Vista previa — así se verá tu tienda. Los cambios no son públicos hasta que publiques.</div>}
      {hasHeader && <StoreHeader header={header} ctx={blockCtx} />}
      {!hasHeader && (
        <Link to={`/tienda/${slug}/mis-pedidos`} data-testid="store-myorders-link"
          className="fixed top-4 left-4 z-30 inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-medium bg-black/40 border border-white/15 text-neutral-200 hover:bg-black/60 backdrop-blur">
          <Package size={14} /> {t("common.myOrders")}
        </Link>
      )}
      <LanguageSwitcher className="fixed bottom-4 right-4 z-30" />
      {!hasHeader && (store.checkout_enabled || store.athm_enabled) && (
        <Link to={`/tienda/${slug}/carrito`} data-testid="store-cart-link"
          className="fixed top-4 right-4 z-30 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-black shadow-lg" style={{ backgroundColor: accent }}>
          <ShoppingCart size={17} weight="fill" /> {t("common.cart")}{cartCount > 0 && <span className="bg-black/25 rounded-full px-1.5 text-xs" data-testid="cart-count">{cartCount}</span>}
        </Link>
      )}
      {collectionCat
        ? <CollectionView products={products} cat={collectionCat} accent={accent} slug={slug} tmpl={tmpl} t={t} onAdded={setCartCount} headingFont={headingFont} titleColor={titleColorG} />
        : (pages.length > 0 || blocks.length > 0)
          ? pageBlocks.map((b) => <AnimatedBlock key={b.id} anim={b.style?.anim}><StoreBlock block={b} ctx={blockCtx} /></AnimatedBlock>)
          : sections.filter((s) => s.enabled !== false).map((s) => renderSection(s.key))}

      {footer && footer.enabled !== false
        ? <StoreFooter footer={footer} ctx={blockCtx} />
        : (
          <footer className="border-t border-white/10 py-6 text-center text-xs text-neutral-600">
            {t("store.poweredBy")} <Link to="/" className="text-neutral-400 hover:text-gold">Hephy 3D</Link>
          </footer>
        )}
    </div>
  );
}

function CollectionView({ products, cat, accent, slug, tmpl, t, onAdded, headingFont, titleColor }) {
  const list = products.filter((p) => catSlug(p.subcategory) === cat);
  const name = list[0]?.subcategory || decodeURIComponent(cat || "").replace(/-/g, " ");
  return (
    <div className="max-w-6xl mx-auto px-6 mt-12 pb-20" data-testid="collection-view">
      <Link to={`/tienda/${slug}`} className="text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-1 mb-4" data-testid="collection-back">← {t("common.back") || "Volver"}</Link>
      <h1 className="text-2xl sm:text-3xl font-bold mb-6 capitalize" style={{ fontFamily: headingFont, color: titleColor || accent }} data-testid="collection-title">{name}</h1>
      {list.length === 0
        ? <div className="border border-dashed border-white/15 rounded-2xl p-16 text-center text-neutral-500" data-testid="collection-empty">{t("store.empty")}</div>
        : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="collection-grid">
            {list.map((p) => (p.catalog_type && p.catalog_type !== "custom3d")
              ? <SimpleProductCard key={p.id} p={p} slug={slug} accent={accent} tmpl={tmpl} t={t} onAdded={onAdded} />
              : <Link key={p.id} to={`/tienda/${slug}/producto/${p.id}`} data-testid={`collection-product-${p.id}`} className={`group overflow-hidden border border-white/10 bg-white/[0.03] hover:border-white/25 transition-all hover:-translate-y-1 ${tmpl === "minimal" ? "rounded-none" : "rounded-2xl"}`}>
                  <div className="aspect-square bg-neutral-900 relative overflow-hidden">
                    {p.base_url ? <img src={assetUrl(p.base_url)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full flex items-center justify-center"><Diamond size={36} weight="duotone" className="text-neutral-700" /></div>}
                  </div>
                  <div className="p-4"><h3 className="font-semibold truncate">{p.name}</h3><div className="flex items-center justify-between mt-2"><span className="text-sm text-neutral-400">{p.price > 0 ? `${t("store.from")} $${Number(p.price).toLocaleString()}` : t("store.customizable")}</span><span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: accent }}>{t("store.personalize")} <ArrowRight size={13} /></span></div></div>
                </Link>)}
          </div>}
    </div>
  );
}


function SimpleProductCard({ p, slug, accent, tmpl, t, onAdded }) {
  const variants = p.variants || [];
  const [variant, setVariant] = useState(variants[0]?.label || "");
  const [busy, setBusy] = useState(false);
  const priceOf = () => {
    const v = variants.find((x) => String(x.label) === String(variant));
    return Number((v && v.price) || p.price || 0);
  };
  const add = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem(`hephy_cart_${slug}`) || undefined;
      const { data } = await api.post("/public/cart/add", { token, slug, product_id: p.id, metal_id: "", variant_label: variant, quantity: 1 }, { withCredentials: false });
      localStorage.setItem(`hephy_cart_${slug}`, data.token);
      onAdded(data.count || 0);
      toast.success(t("store.addedToCart", "Añadido al carrito"));
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`store-product-${p.id}`} className={`group overflow-hidden border border-white/10 bg-white/[0.03] transition-all ${tmpl === "minimal" ? "rounded-none" : "rounded-2xl"}`}>
      <Link to={`/tienda/${slug}/producto/${p.id}`} className="block aspect-square bg-neutral-900 relative overflow-hidden">
        {p.image_url || p.base_url
          ? <img src={assetUrl(p.image_url || p.base_url)} alt={p.name} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center"><Package size={36} weight="duotone" className="text-neutral-700" /></div>}
        {p.catalog_type === "digital" && <span className="absolute top-3 left-3 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold text-black" style={{ backgroundColor: accent }}>Digital</span>}
        {p.sale_percent > 0 && <span className="absolute top-3 right-3 text-[10px] font-bold px-2 py-1 rounded-full bg-rose-500 text-white" data-testid={`sale-badge-${p.id}`}>−{p.sale_percent}%</span>}
      </Link>
      <div className="p-4">
        <Link to={`/tienda/${slug}/producto/${p.id}`} className="block hover:opacity-80"><h3 className="font-semibold truncate">{p.name}</h3></Link>
        {variants.length > 0 && (
          <select value={variant} onChange={(e) => setVariant(e.target.value)} data-testid={`variant-select-${p.id}`}
            className="mt-2 w-full h-9 rounded-lg bg-white/5 border border-white/15 px-2 text-sm text-neutral-200">
            {variants.map((v) => <option key={v.label} value={v.label} className="bg-[#1a1a1a]">{p.variant_name || "Opción"}: {v.label}{v.price ? ` — $${Number(v.price).toLocaleString()}` : ""}</option>)}
          </select>
        )}
        <div className="flex items-center justify-between mt-2 gap-2">
          <span className="text-sm text-neutral-300 font-semibold">{p.sale_percent > 0 ? <><span style={{ color: accent }}>${Math.round(priceOf() * (1 - p.sale_percent / 100) * 100) / 100}</span> <span className="line-through opacity-50 font-normal">${priceOf().toLocaleString()}</span></> : `$${priceOf().toLocaleString()}`}</span>
          <button onClick={add} disabled={busy} data-testid={`add-cart-${p.id}`}
            className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-full text-black disabled:opacity-60" style={{ backgroundColor: accent }}>
            <ShoppingCart size={13} weight="fill" /> {busy ? "…" : t("store.addToCart", "Añadir")}
          </button>
        </div>
      </div>
    </div>
  );
}
